/**
 * Behind The Collection — "unlock the story" reveal controller.
 *
 * Dependency-free and progressively enhanced:
 *   - The pre-reveal hidden state is gated behind [data-story-reveal-ready] on
 *     <html>, which is set by a tiny inline script in story-hero BEFORE first
 *     paint (so there's no flash) and only when motion is allowed. If JS is
 *     disabled the attribute is never set, so all content stays visible.
 *   - prefers-reduced-motion: this controller does nothing (content is already
 *     visible, no lock, no transforms).
 *
 * Responsibilities:
 *   - Reveal [data-reveal] elements as they scroll into view (one-shot).
 *   - Stagger grouped children via --reveal-index.
 *   - Auto-tag sections we don't own (e.g. the mood board) for a whole-section
 *     reveal, lightly staggering any tiles.
 *   - Drive the hero "unlock" gesture (click / first scroll / keyboard).
 */
(function () {
  var hero = document.querySelector('[data-story-page]');
  if (!hero) return;

  // Reading progress runs regardless of motion preference (it's not animation,
  // just a scroll indicator).
  setupProgress();

  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  var heroSection = hero.closest('.shopify-section') || hero;
  var container = heroSection.parentElement || document.body;

  // --- Auto-tag sections without fine-grained targets (e.g. mood board) ------
  var topSections = Array.prototype.filter.call(container.children, function (el) {
    return el.classList && el.classList.contains('shopify-section');
  });

  topSections.forEach(function (sec) {
    if (sec.querySelector('[data-reveal]')) return;
    var inner = sec.firstElementChild || sec;
    var tiles = sec.querySelectorAll('.mood-board__cell');
    if (tiles.length > 1) {
      Array.prototype.forEach.call(tiles, function (tile, i) {
        tile.setAttribute('data-reveal', '');
        tile.style.setProperty('--reveal-index', i);
      });
    } else {
      inner.setAttribute('data-reveal', '');
    }
  });

  // Index any stagger groups that weren't indexed inline in Liquid.
  container.querySelectorAll('[data-reveal-stagger]').forEach(function (group) {
    Array.prototype.forEach.call(group.querySelectorAll('[data-reveal]'), function (el, i) {
      if (!el.style.getPropertyValue('--reveal-index')) {
        el.style.setProperty('--reveal-index', i);
      }
    });
  });

  var targets = container.querySelectorAll('[data-reveal]');

  // --- Reveal on scroll ------------------------------------------------------
  function reveal(el) {
    if (el.hasAttribute('data-revealed')) return;
    el.setAttribute('data-revealed', '');
    el.addEventListener('transitionend', function done() {
      el.style.willChange = '';
      el.removeEventListener('transitionend', done);
    });
  }

  var io = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        reveal(entry.target);
        io.unobserve(entry.target);
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
  );

  var locked = hero.hasAttribute('data-unlock');

  function observeRest() {
    Array.prototype.forEach.call(targets, function (el) {
      if (el.hasAttribute('data-revealed')) return;
      // While locked, the hero's own content waits for the unlock gesture.
      if (locked && hero.contains(el)) return;
      io.observe(el);
    });
  }

  // --- Hero unlock -----------------------------------------------------------
  if (locked) {
    hero.setAttribute('data-locked', '');

    var cover = hero.querySelector('.story-hero__lock');
    var button = hero.querySelector('.story-hero__lock-button');
    var scrollOpts = { passive: true, once: true };

    var onScrollIntent = function () {
      unlock();
    };

    var unlock = function () {
      if (!hero.hasAttribute('data-locked')) return;
      hero.removeAttribute('data-locked');
      hero.setAttribute('data-unlocked', '');

      window.removeEventListener('wheel', onScrollIntent, scrollOpts);
      window.removeEventListener('touchmove', onScrollIntent, scrollOpts);
      window.removeEventListener('scroll', onScrollIntent, scrollOpts);

      // Reveal the hero's own content now (above the fold), then hide the cover.
      Array.prototype.forEach.call(hero.querySelectorAll('[data-reveal]'), reveal);

      if (cover) {
        cover.addEventListener('transitionend', function hide() {
          cover.style.display = 'none';
          cover.removeEventListener('transitionend', hide);
        });
      }
    };

    if (button) button.addEventListener('click', unlock);
    if (cover) cover.addEventListener('click', unlock);
    window.addEventListener('wheel', onScrollIntent, scrollOpts);
    window.addEventListener('touchmove', onScrollIntent, scrollOpts);
    window.addEventListener('scroll', onScrollIntent, scrollOpts);
  }

  observeRest();

  // --- Reading progress ------------------------------------------------------
  function setupProgress() {
    var bar = document.querySelector('.story-progress');
    if (!bar) return;
    var ticking = false;

    function update() {
      ticking = false;
      var scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 4) {
        bar.classList.remove('is-active');
        return;
      }
      var p = window.scrollY / scrollable;
      if (p < 0) p = 0;
      if (p > 1) p = 1;
      bar.style.setProperty('--story-progress', p.toFixed(4));
      bar.classList.add('is-active');
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }
})();
